import React, { Component } from 'react';
import { inject, observer } from 'mobx-react';
import { Field, InputGroup, Select } from 'core/form';
import { PopoverInteractionKind } from '@blueprintjs/core';
import { CREDENTIAL_TYPES } from '@kentik/ui-shared/credentials/constants';
import { withRouter } from 'react-router-dom';
import { Button, Box, Heading, ButtonGroupTab, ButtonGroupTabs, Card, Popover, Flex, Text } from 'core/components';
import { Table } from 'core/components/table';

import CredentialDialog from 'app/views/settings/credentials/CredentialDialog';
import CloudExportAzureSpnCollection from 'app/stores/clouds/azure/CloudExportAzureSpnCollection';

const TAB_CUSTOM_APP_REGISTRATION = 'app_registration';
const TAB_CUSTOM_KENTIK_ENTERPRISE_APPLICATION = 'kentik_enterprise_application';

@withRouter
@inject('$cloudExportWizard', '$credentials')
@observer
class AzureAuthenticationPanel extends Component {
  azureSpnCollection = new CloudExportAzureSpnCollection();

  state = {
    isLoading: false,
    popoverIsOpen: false,
    credentialModel: null,
    showAddCredentialDialog: false,
    isCredentialsOptionsLoading: true
  };

  async componentDidMount() {
    const { $credentials, $cloudExportWizard, form } = this.props;

    $credentials.groupedCollection.fetch().then(() => {
      this.setState({ isCredentialsOptionsLoading: false });
    });

    if (form) {
      $cloudExportWizard.setForm(form);
    }

    const selectedKentikAzureSpnId = $cloudExportWizard.getFieldValue('properties.kentik_azure_spn_id');

    if (selectedKentikAzureSpnId) {
      // load and set current exporter azure spn record
      this.azureSpnCollection.fetch({ force: true }).then(() => {
        this.handleSelectPreviousAzureSpnRecord(this.azureSpnCollection.find(selectedKentikAzureSpnId));
      });
    }
  }

  handleTabChange = (tabId) => {
    const { $cloudExportWizard } = this.props;

    $cloudExportWizard.setFieldValue('properties.azure_api_access_method', tabId);
  };

  handleSelectPreviousAzureSpnRecord = (model) => {
    const { $cloudExportWizard } = this.props;

    $cloudExportWizard.setFieldValue('properties.kentik_azure_spn_id', model.get('id'));
    $cloudExportWizard.setFieldValue('properties.azure_application_id', model.get('spn_app_id'));
    $cloudExportWizard.setFieldValue('properties.azure_application_directory_id', model.get('spn_tenant_id'));
    $cloudExportWizard.setFieldValue(
      'properties.kentik_credential_secret_name',
      model.get('metadata.mn_credential_vault_name')
    );

    this.setState({ popoverIsOpen: false });
  };

  handleCreateNewAzureSpnRecord = () => {
    const { $cloudExportWizard } = this.props;

    $cloudExportWizard.setFieldValue('properties.kentik_azure_spn_id', '');
    $cloudExportWizard.setFieldValue('properties.azure_application_id', '');
    $cloudExportWizard.setFieldValue('properties.azure_application_directory_id', '');
    $cloudExportWizard.setFieldValue('properties.kentik_credential_secret_name', '');
  };

  handleLoadPreviousAzureSpnRecords = async () => {
    this.setState((prevState) => ({ popoverIsOpen: !prevState.popoverIsOpen }));
    await this.azureSpnCollection.fetch({ force: true });
  };

  handleSaveNewAzureSpnRecord = async () => {
    const { $cloudExportWizard } = this.props;

    this.setState({ isLoading: true });

    const azureSpnModel = this.azureSpnCollection.forge({
      spn_app_id: $cloudExportWizard.getFieldValue('properties.azure_application_id'),
      spn_tenant_id: $cloudExportWizard.getFieldValue('properties.azure_application_directory_id'),
      kentik_credential_name: $cloudExportWizard.getFieldValue('properties.kentik_credential_secret_name')
    });

    try {
      await azureSpnModel.save();
      $cloudExportWizard.setFieldValue('properties.kentik_azure_spn_id', azureSpnModel.get('id'));
    } catch (error) {
      console.error('Unable to save azure spn record', error);
    }

    this.setState({ isLoading: false });
  };

  get previousAzureSpnColumns() {
    return [
      {
        label: 'Application ID',
        name: 'spn_app_id',
        ellipsis: false
      },
      {
        label: 'Directory ID',
        name: 'spn_tenant_id',
        ellipsis: false
      },
      {
        label: 'Kentik Credential Secret',
        name: 'metadata',
        ellipsis: false,
        renderer: ({ value }) => <Text>{value?.mn_credential_vault_name}</Text>
      }
    ];
  }

  handleCloseCredentialsDialog = (formValues) => {
    const { $cloudExportWizard } = this.props;
    this.setState({ showAddCredentialDialog: false });

    if (formValues) {
      const { name, tokens } = formValues;
      $cloudExportWizard.setFieldValue('properties.kentik_credential_secret_name', `${name}.${tokens[0].key}`);
    }
  };

  handleClickAddCredentialDialog = () => {
    const { $credentials } = this.props;

    this.setState({
      showAddCredentialDialog: true,
      credentialModel: $credentials.groupedCollection.forge({ type: CREDENTIAL_TYPES.cloud })
    });
  };

  render() {
    const { $cloudExportWizard, $credentials } = this.props;
    const { isLoading, popoverIsOpen, showAddCredentialDialog, credentialModel, isCredentialsOptionsLoading } =
      this.state;

    const isPrevSpnSelected = $cloudExportWizard.getFieldValue('properties.kentik_azure_spn_id');
    const allowSave =
      !isPrevSpnSelected &&
      $cloudExportWizard.getField('properties.azure_application_id')?.valid &&
      $cloudExportWizard.getField('properties.kentik_credential_secret_name')?.valid &&
      $cloudExportWizard.getField('properties.azure_application_directory_id')?.valid;

    return (
      <Flex flex={1} flexDirection="column" gap="1">
        <ButtonGroupTabs
          omitTabsWrapper
          onChange={this.handleTabChange}
          selectedTabId={$cloudExportWizard.getFieldValue('properties.azure_api_access_method')}
        >
          <ButtonGroupTab
            id={TAB_CUSTOM_KENTIK_ENTERPRISE_APPLICATION}
            text="Kentik enterprise application"
            panel={<Text small>Kentik subscription id will be used to api requests.</Text>}
          />
          <ButtonGroupTab
            id={TAB_CUSTOM_APP_REGISTRATION}
            title="Custom app registration"
            panel={
              <Card bg="backgrounds.app">
                <Flex flexDirection="column" gap={1} p={2}>
                  <Flex justifyContent="space-between">
                    <Text as="div" fontWeight="bold" mb={1}>
                      Details
                    </Text>

                    <Flex gap={1}>
                      {isPrevSpnSelected && (
                        <Button small text="Create new" onClick={this.handleCreateNewAzureSpnRecord} />
                      )}
                      <Popover
                        usePortal
                        isOpen={popoverIsOpen}
                        position="right"
                        interactionKind={PopoverInteractionKind.CLICK}
                        minimal={false}
                        modifiers={{
                          flip: { enabled: false },
                          preventOverflow: { enabled: true, boundariesElement: 'window' }
                        }}
                        content={
                          <Box flex={1} p={2} minWidth={500}>
                            <Heading level={4}>Azure App Registrations</Heading>
                            <Flex flexDirection="column" border="thin">
                              <Table
                                collection={this.azureSpnCollection}
                                columns={this.previousAzureSpnColumns}
                                onRowClick={(model, e) => {
                                  e.stopPropagation();
                                  this.handleSelectPreviousAzureSpnRecord(model);
                                }}
                              />
                            </Flex>
                          </Box>
                        }
                        target={
                          <Button
                            small
                            icon="archive"
                            text="Saved App Registrations"
                            onClick={this.handleLoadPreviousAzureSpnRecords}
                          />
                        }
                      />
                    </Flex>
                  </Flex>

                  <Field name="properties.azure_application_id" width="100%" disabled={isPrevSpnSelected} large>
                    <InputGroup />
                  </Field>

                  <Field
                    name="properties.azure_application_directory_id"
                    width="100%"
                    disabled={isPrevSpnSelected}
                    large
                  >
                    <InputGroup />
                  </Field>

                  {!isPrevSpnSelected && (
                    <Flex justifyContent="space-between" gap={6}>
                      <Field
                        flex="1"
                        name="properties.kentik_credential_secret_name"
                        options={$credentials.getCredentialTokenOptionsByTypes([CREDENTIAL_TYPES.cloud])}
                        large
                      >
                        <Select loading={isCredentialsOptionsLoading} menuWidth="100%" />
                      </Field>
                      <Flex flexDirection="column" alignItems="flex-end">
                        <Button
                          onClick={this.handleClickAddCredentialDialog}
                          text="Create New Secret"
                          mt="auto"
                          mb={1}
                        />
                      </Flex>
                    </Flex>
                  )}

                  {credentialModel && (
                    <CredentialDialog
                      model={credentialModel}
                      isOpen={showAddCredentialDialog}
                      onClose={this.handleCloseCredentialsDialog}
                      hideTypeSelector
                    />
                  )}

                  {!isPrevSpnSelected && (
                    <Flex justifyContent="flex-end">
                      <Button
                        disabled={!allowSave}
                        loading={isLoading}
                        onClick={this.handleSaveNewAzureSpnRecord}
                        text="Save App Registration"
                      />
                    </Flex>
                  )}
                </Flex>
              </Card>
            }
          />
        </ButtonGroupTabs>
      </Flex>
    );
  }
}

export default AzureAuthenticationPanel;
